import { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight, Globe } from 'lucide-react';
import { useHARStore } from '@/stores/har-store';
import { formatBytes, formatTime, getFileName, extractDomain, getStatusColor } from '@har-insight/parser';
import type { HAREntry } from '@har-insight/parser';
import { cn } from '@/lib/utils';

interface DomainGroup {
  domain: string;
  entries: HAREntry[];
  totalSize: number;
  totalTime: number;
  errorCount: number;
}

export function DomainGrouping() {
  const { filteredEntries, selectedEntry, selectEntry } = useHARStore();
  const [expandedDomains, setExpandedDomains] = useState<Set<string>>(new Set());

  const groups = useMemo(() => {
    const groupMap = new Map<string, DomainGroup>();

    filteredEntries.forEach(entry => {
      const domain = extractDomain(entry.request.url);
      const existing = groupMap.get(domain);

      if (existing) {
        existing.entries.push(entry);
        existing.totalSize += entry.response.content.size || 0;
        existing.totalTime += entry.time || 0;
        if (entry.response.status >= 400) existing.errorCount++;
      } else {
        groupMap.set(domain, {
          domain,
          entries: [entry],
          totalSize: entry.response.content.size || 0,
          totalTime: entry.time || 0,
          errorCount: entry.response.status >= 400 ? 1 : 0,
        });
      }
    });

    return Array.from(groupMap.values()).sort((a, b) => b.entries.length - a.entries.length);
  }, [filteredEntries]);

  const toggleDomain = (domain: string) => {
    setExpandedDomains(prev => {
      const next = new Set(prev);
      if (next.has(domain)) {
        next.delete(domain);
      } else {
        next.add(domain);
      }
      return next;
    });
  };

  const expandAll = () => {
    setExpandedDomains(new Set(groups.map(g => g.domain)));
  };

  const collapseAll = () => {
    setExpandedDomains(new Set());
  };

  if (groups.length === 0) {
    return (
      <div className="flex items-center justify-center h-[400px] text-slate-400 dark:text-slate-500">
        No requests to display
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Controls */}
      <div className="flex justify-end gap-2 mb-2">
        <button
          onClick={expandAll}
          className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
        >
          Expand all
        </button>
        <span className="text-slate-300 dark:text-slate-700">|</span>
        <button
          onClick={collapseAll}
          className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
        >
          Collapse all
        </button>
      </div>

      {/* Domain groups */}
      <div className="space-y-1 max-h-[600px] overflow-auto">
        {groups.map(group => {
          const isExpanded = expandedDomains.has(group.domain);

          return (
            <div key={group.domain} className="border border-slate-200 dark:border-slate-800 rounded-lg overflow-hidden">
              {/* Domain header */}
              <button
                onClick={() => toggleDomain(group.domain)}
                className={cn(
                  'w-full flex items-center gap-3 p-3 text-left transition-colors',
                  'hover:bg-slate-100 dark:hover:bg-slate-800/50',
                  isExpanded && 'bg-slate-50 dark:bg-slate-800/30'
                )}
              >
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                )}

                <Globe className="w-4 h-4 text-slate-400 flex-shrink-0" />

                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate block">
                    {group.domain}
                  </span>
                </div>

                <div className="flex items-center gap-4 text-xs text-slate-500 flex-shrink-0">
                  <span>{group.entries.length} req{group.entries.length !== 1 ? 's' : ''}</span>
                  <span>{formatBytes(group.totalSize)}</span>
                  <span>{formatTime(group.totalTime)}</span>
                  {group.errorCount > 0 && (
                    <span className="text-red-500 dark:text-red-400">
                      {group.errorCount} error{group.errorCount !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </button>

              {/* Entries */}
              {isExpanded && (
                <div className="border-t border-slate-200 dark:border-slate-800">
                  {group.entries.map((entry, index) => {
                    const isSelected = selectedEntry?._id === entry._id;
                    const statusColor = getStatusColor(entry.response.status);

                    return (
                      <div
                        key={entry._id || index}
                        onClick={() => selectEntry(entry)}
                        className={cn(
                          'flex items-center gap-3 px-3 py-2 pl-10 cursor-pointer transition-colors',
                          'hover:bg-slate-100 dark:hover:bg-slate-800/30',
                          isSelected && 'bg-brand-50 dark:bg-brand-500/10',
                          index < group.entries.length - 1 && 'border-b border-slate-100 dark:border-slate-800/50'
                        )}
                      >
                        <span className={cn('text-xs font-mono w-8', statusColor)}>
                          {entry.response.status}
                        </span>
                        <span className="text-xs px-1.5 py-0.5 bg-slate-200 dark:bg-slate-700 rounded">
                          {entry.request.method}
                        </span>
                        <span className="text-sm text-slate-600 dark:text-slate-400 truncate flex-1">
                          {getFileName(entry.request.url)}
                        </span>
                        <span className="text-xs text-slate-400 flex-shrink-0">
                          {formatBytes(entry.response.content.size || 0)}
                        </span>
                        <span className={cn(
                          'text-xs font-mono flex-shrink-0',
                          entry.time > 1000 ? 'text-yellow-600 dark:text-yellow-400' : 'text-slate-400',
                          entry.time > 3000 && 'text-red-600 dark:text-red-400'
                        )}>
                          {formatTime(entry.time || 0)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
